<template>
  <div id="resizible-wrapper" class="container detail-shell px-4 relative">
    <PageHeader
      title="Live Clipping"
      subtitle="Create shareable clips from your live streams in seconds."
      mobile-inline
      :usage="!loadingStatus ? highlightHeaderUsage : null"
    >
      <Tooltip v-if="!loadingStatus && isAccessHighlight" :active="atHighlightLimit" wrap-trigger :html="true" :hide="1000">
        <FormButton class="gap-1" type="main" size="md" :disabled="atHighlightLimit" @click="$showModal('modal-add-highlight')">
          <icon-base class="mr-1">
            <icon-plus />
          </icon-base>
          <span> New <span class="hidden md:inline">Live Clipping</span> </span>
        </FormButton>
        <template #content>
          <span class="text-surface-8 text-sm" v-html="highlightCreateDisabledTitle" />
        </template>
      </Tooltip>
    </PageHeader>
    <Spinner v-if="loadingStatus" text="Loading your clips…" classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />
    <main v-else>
      <section v-if="!isAccessHighlight">
        <EmptyState title="Upgrade to unlock Live Clipping"
          subtitle="Capture and download shareable clips from your live streams in seconds. It's not included in your current plan.">
          <template #icon>
            <icon-base width="28" height="28" fill="none">
              <icon-lock />
            </icon-base>
          </template>
          <template #actions>
            <div class="flex w-full max-w-64 flex-col mx-auto">
              <FormButton type="secondary" full-width class="mb-2 justify-center" target="_blank"
                href="https://docs.castr.com/en/articles/8856289-highlights">
                <icon-base class="mr-1" fill="none">
                  <icon-book-outline />
                </icon-base>
                How Live Clipping works
              </FormButton>
              <FormButton full-width class="mb-2 justify-center" type="secondary" @click="$showModal('modal-demo-video')">
                <icon-base class="mr-1" fill="none">
                  <icon-play-outline />
                </icon-base>
                Live Clipping Demo
              </FormButton>
              <FormButton type="main" size="md" full-width class="justify-center" to="/subscribe">
                Upgrade now
              </FormButton>
            </div>
          </template>
        </EmptyState>
      </section>

      <section v-else-if="this.rawHighlight && (this.rawHighlight.length > 0)">
        <div>
          <ResponsiveToolbar class="my-3">
            <template v-if="showListControls">
            <DropDownMenu type="secondary" position="left">
              <template #toggle-button>
                <span class="text-surface-8">Sort by:&nbsp;</span>
                <span class="text-white">{{ sortBy }}</span>
                <icon-base class="!ml-1 shrink-0 text-surface-8">
                  <icon-chevron-up-down />
                </icon-base>
              </template>
              <template #menu-items>
                <DropDownMenuItem v-for="opt in sortOptions" :key="opt" :active="sortBy === opt" @click="changeSortBy(opt)">
                  {{ opt }}
                  <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === opt" fill="none">
                    <icon-checkmark />
                  </icon-base>
                </DropDownMenuItem>
              </template>
            </DropDownMenu>
            <ListingSearchInput v-model="searchWidgetInput" />
            </template>
            <ViewModeSwitcher class="shrink-0" />
          </ResponsiveToolbar>
        </div>
        <div class="mt-3 grid auto-rows-fr"
          :class="shouldShowCompactModeStreams ? '!grid-cols-1 gap-1.5' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'">
          <highlight-card-view
            v-for="h in highlight"
            :key="h._id"
            @delete-highlight="onDeleteHighlight"
            :streamhighlight="h"
            :compact-mode="shouldShowCompactModeStreams"
          />
          <ListingCreateTile
            v-if="!searchWidgetInput"
            label="Live Clipping"
            :view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
            :used="rawHighlight.length"
            :limit="isAccessHighlight"
            :limit-reached="atHighlightLimit"
            @create="$showModal('modal-add-highlight')"
          />
        </div>
      </section>

      <section v-else>
        <EmptyState title="Clip highlights from your live streams"
          subtitle="Capture shareable moments from a live stream in seconds, then reuse them for social, replays, or post-event edits.">
          <template #icon>
            <icon-base width="28" height="28" fill="none">
              <icon-highlighter />
            </icon-base>
          </template>
          <template #actions>
            <div class="flex flex-col mx-auto max-w-64">
              <FormButton type="secondary" class="mb-2 justify-center" target="_blank"
                href="https://docs.castr.com/en/articles/8856289-highlights">
                <icon-base class="mr-1" fill="none">
                  <icon-book-outline />
                </icon-base>
                How Live Clipping works
              </FormButton>
              <FormButton class="mb-2 justify-center" target="_blank" type="secondary" @click="$showModal('modal-demo-video')">
                <icon-base class="mr-1" fill="none">
                  <icon-play-outline />
                </icon-base>
                Live Clipping Demo
              </FormButton>
              <Tooltip :active="atHighlightLimit" wrap-trigger :html="true" :hide="1000">
                <FormButton class="gap-1 justify-center shrink-0 w-full" size="md" type="main" :disabled="atHighlightLimit" @click="$showModal('modal-add-highlight')">
                  <icon-base class="mr-2" :width="12" :height="12" width-view-box="12" height-view-box="12">
                    <icon-plus-tiny />
                  </icon-base>
                  New <span class="hidden md:inline">Live Clipping</span>
                </FormButton>
                <template #content>
                  <span class="text-surface-8 text-sm" v-html="highlightCreateDisabledTitle" />
                </template>
              </Tooltip>
            </div>
          </template>
        </EmptyState>
      </section>

    </main>
    <confirm-modal modal-type="danger" message="Would you like to delete this Live Clipping and all of its content?"
      @modal-confirm="onHighlightDeleteConfirm" />
    <add-highlight-modal @highlight-created="createdHighlight" :highlight="highlight" />
    <demo-video-modal videoid="E5NGhYcn07xZKW-9qMSTy" />
  </div>
</template>

<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import _ from "lodash";
import { mapGetters } from 'vuex'
import AddHighlightModal from "@/components/modals/AddHighlightModal.vue"
import Spinner from "@/components/ui/Spinner.vue";
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import HighlightCardView from '@/components/ui/HighlightCardView.vue';
import StreamService from '@/services/StreamService';
import HighlightService from '@/services/HighlightService';
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue';
import IconCheckmark from '@/components/icon/IconCheckmark.vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'
import EmptyState from '@/components/ui/EmptyState.vue'
import IconLock from '@/components/icon/IconLock.vue'
import IconHighlighter from '@/components/icon/IconHighlighter.vue'
import IconBookOutline from '@/components/icon/IconBookOutline.vue'
import IconPlayOutline from '@/components/icon/IconPlayOutline.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconPlusTiny from '@/components/icon/IconPlusTiny.vue'
import { track, EVENTS } from '@/lib/tracking'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue'
import ViewModeSwitcher from '@/components/ui/ViewModeSwitcher.vue'
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue'
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue'
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort'

export default {
  name: "ManageHighlights",
  setup() {
    const { sortBy, setSort } = usePersistedSort('castr.sort.highlights', DATE_NAME_SORT, 'Newest')
    return { sortBy, setSort, sortOptions: DATE_NAME_SORT }
  },
  components: {
    DropDownMenu,
    DropDownMenuItem,
    FormButton,
    IconBase,
    IconChevronUpDown,
    IconCheckmark,
    Spinner,
    ConfirmModal,
    AddHighlightModal,
    DemoVideoModal,
    HighlightCardView,
    Tooltip,
    PageHeader,
    EmptyState,
    IconLock,
    IconHighlighter,
    IconBookOutline,
    IconPlayOutline,
    IconPlus,
    IconPlusTiny,
    ListingCreateTile,
    ViewModeSwitcher,
    ResponsiveToolbar,
    ListingSearchInput,
  },
  computed: {
    ...mapGetters({
      loadingStatus: "Ui/loadingStatus",
      highlight: "Highlight/highlight",
      rawHighlight: "Highlight/rawHighlight",
      allInOneStreams: "Streams/allInOneStreams",
      subscriptions: "User/subscriptions",
    }),
    isAccessHighlight() {
      let sub = null
      this.subscriptions.some((item) => {
        if (item.enabled && item.category === 'live') sub = item
      })
      let isValidPlanOverrideDefinition = null
      let isValidPlanDefinition = false
      if (sub) {
        if (sub?.package?.definition?.maxHighlightCount) isValidPlanDefinition = sub.package.definition.maxHighlightCount
        if (sub?.definitionOverride?.maxHighlightCount) isValidPlanOverrideDefinition = sub.definitionOverride.maxHighlightCount
      }
      if (isValidPlanOverrideDefinition) return isValidPlanOverrideDefinition
      return isValidPlanDefinition
    },
    highlightHeaderUsage() {
      const used = this.rawHighlight?.length ?? 0
      if (!used) return null
      const meta = {
        used,
        label: 'clips',
        labelSingular: 'clip',
      }
      const limit = typeof this.isAccessHighlight === 'number' && this.isAccessHighlight > 0
        ? this.isAccessHighlight
        : null
      if (limit) meta.limit = limit
      return meta
    },
    atHighlightLimit() {
      const limit = typeof this.isAccessHighlight === 'number' && this.isAccessHighlight > 0
        ? this.isAccessHighlight
        : null
      if (!limit) return false
      return (this.rawHighlight?.length ?? 0) >= limit
    },
    highlightCreateDisabledTitle() {
      if (this.atHighlightLimit) return planLimitReachedMessage('Clip')
      return ''
    },
    shouldShowCompactModeStreams() {
      return this.$store.getters['Ui/shouldShowCompactModeStreams']
    },
    showListControls() {
      return (this.rawHighlight?.length ?? 0) > 2
    },
  },
  async mounted() {
    if (!this.$store.getters['Highlight/isLoaded']) {
      this.$store.dispatch('Ui/setLoadingStatus', true)
    } else {
      this.$store.dispatch('Ui/setLoadingStatus', false)
    }
    try {
      this.$store.dispatch('Highlight/setSortOption', this.sortBy)
      await this.$store.dispatch('Highlight/getHighlight')
      await this.$store.dispatch('Streams/getStreams');

      (this.highlight || []).map(h => {
        let streamobj = _.cloneDeep(this.allInOneStreams.filter(stream => stream._id === h.stream))
        h.streamobj = streamobj
      });
      this.rawHighlight = this.highlight;
    } finally {
      this.$store.dispatch('Ui/setLoadingStatus', false)
    }
  },
  methods: {
    changeSortBy(val) {
      this.setSort(val);
      this.$store.dispatch('Highlight/setSortOption', val)
    },
    async onHighlightDeleteConfirm() {
      // pop stream out of the list
      // const index = this.streams.indexOf(this.deleteModalConfiguredStream);
      // this.streams = _.concat(
      // 	this.streams.slice(0, index),
      // 	this.streams.slice(index + 1)
      // );

      try {
        await HighlightService.deleteHighlight(this.deleteModalConfiguredStream._id, this.deleteModalConfiguredStream.name);
        // Force refresh to remove deleted highlight
        await this.$store.dispatch('Highlight/getHighlight', true)
        track(EVENTS.HIGHLIGHT_DELETED, {});
        this.$notify({ group: 'info', text: 'Live clipping deleted.' });
      } catch (err) {
        this.$notify({ group: 'error', title: err.error, text: err.message });
        // push stream back to list
        // this.onNewStream(this.deleteModalConfiguredStream);
      }

      this.deleteModalConfiguredStream = null;
    },
    createdHighlight(h) {
      this.hasHighlight = true;
      this.$router.push({ path: '/highlight/' + h._id }).catch(() => { });
      //   this.selectPlaylist(0);
    },
    onDeleteHighlight(stream) {
      this.deleteModalConfiguredStream = stream;
      showModal('modal-confirm');
    },
  },
  watch: {
    searchWidgetInput() {
        this.$store.dispatch('Highlight/searchHighlight', this.searchWidgetInput)
    },
  },
  data() {
    return {
      playlistIndex: 0,
      hasHighlight: false,
      deleteModalConfiguredStream: null,
      searchWidgetInput: '',
      allHighlights: null
    };
  },
}

</script>
